import { useState } from 'react';
import { Icon } from '@/components/atoms';
import { BatchList } from '@/components/organisms/Batch';
import { useInventoryBatchesList, useLowStockBatches, useExpiringBatches } from '@/hooks/useErpApi';

export default function BatchListPage() {
  const [activeView, setActiveView] = useState<'all' | 'low-stock' | 'expiring'>('all');

  const { data: allBatchesData } = useInventoryBatchesList();
  const { data: lowStockBatchesData } = useLowStockBatches(10);
  const { data: expiringBatchesData } = useExpiringBatches(30);

  // Helper to extract array from various API response shapes
  function extractArray<T>(data: unknown): T[] {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const d = data as any;
    if (d?.data && Array.isArray(d.data)) return d.data;
    if (d?.data?.data && Array.isArray(d.data.data)) return d.data.data;
    return [];
  }

  const allBatches = extractArray<any>(allBatchesData);
  const lowStockBatches = extractArray<any>(lowStockBatchesData);
  const expiringBatches = extractArray<any>(expiringBatchesData);

  const totalBatches = allBatches.length;
  const lowStockCount = lowStockBatches.length;
  const expiringCount = expiringBatches.length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Batch Management</h1>
          <p className="text-neutral-600 mt-2">
            Track inventory batches, expiry dates, and transaction history
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <button
          onClick={() => setActiveView('all')}
          className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-all text-left ${
            activeView === 'all'
              ? 'border-primary-500 shadow-md'
              : 'border-neutral-200 hover:border-primary-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Total Batches</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="Package" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalBatches}</p>
          <p className="text-xs text-neutral-500 mt-1">All inventory batches</p>
        </button>

        <button
          onClick={() => setActiveView('low-stock')}
          className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-all text-left ${
            activeView === 'low-stock'
              ? 'border-error-500 shadow-md'
              : 'border-neutral-200 hover:border-error-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Low Stock</p>
            <div className="bg-error-50 p-2 rounded-lg">
              <Icon name="CircleAlert" size={20} className="text-error-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-error-700">{lowStockCount}</p>
          <p className="text-xs text-neutral-500 mt-1">Batches below 10% quantity</p>
        </button>

        <button
          onClick={() => setActiveView('expiring')}
          className={`bg-white rounded-lg shadow-sm border-2 p-6 transition-all text-left ${
            activeView === 'expiring'
              ? 'border-warning-500 shadow-md'
              : 'border-neutral-200 hover:border-warning-300'
          }`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Expiring Soon</p>
            <div className="bg-warning-50 p-2 rounded-lg">
              <Icon name="Clock" size={20} className="text-warning-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-warning-700">{expiringCount}</p>
          <p className="text-xs text-neutral-500 mt-1">Expiring within 30 days</p>
        </button>
      </div>

      {/* View Toggle */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView('all')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              activeView === 'all'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            All Batches
          </button>
          <button
            onClick={() => setActiveView('low-stock')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              activeView === 'low-stock'
                ? 'bg-error-600 text-white shadow-sm'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            Low Stock
          </button>
          <button
            onClick={() => setActiveView('expiring')}
            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 ${
              activeView === 'expiring'
                ? 'bg-warning-600 text-white shadow-sm'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            Expiring Soon
          </button>
        </div>
      </div>

      {/* Batch List */}
      <BatchList />

      {/* Info Panel */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-info-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-info-900 mb-1">Batch Management Tips</h4>
            <ul className="text-sm text-info-700 space-y-1">
              <li>• Batches are automatically created when goods are received (GRN)</li>
              <li>• FEFO (First Expiry, First Out) is applied automatically during sales</li>
              <li>• Monitor expiring batches regularly to minimize waste</li>
              <li>• Click on any batch row to view detailed transaction history</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
